import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner, Form, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  ClipboardCheck, ChevronRight, ChevronLeft, Check, Search,
  Plus, X, Zap, Shield, Eye, HelpCircle, Clock, Users,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./Assessments.scss";

const STEPS = [
  { label: "Basic Info", key: "info" },
  { label: "Questions", key: "questions" },
  { label: "Settings", key: "settings" },
  { label: "Assignment", key: "assignment" },
  { label: "Review", key: "review" },
];

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned Exam" };
const QUESTION_TYPE_LABELS = {
  single_choice: "Single Choice", multi_select: "Multi Select", true_false: "True/False",
  text_answer: "Text", fill_blank: "Fill Blank", image_mcq: "Image MCQ", code_conceptual: "Code",
};

const emptyForm = {
  title: "",
  description: "",
  instructions: "",
  selectionMode: "manual",
  questions: [],
  dynamicConfig: { rules: [], totalQuestions: 0, avoidRecentDays: 0 },
  isSectioned: false,
  sections: [],
  mode: "practice",
  timeLimit: 0,
  maxAttempts: 0,
  passingPercentage: 0,
  negativeMarking: false,
  shuffleQuestions: false,
  shuffleOptions: false,
  showResultInstantly: true,
  showExplanation: true,
  showCorrectAnswers: false,
  allowReview: true,
  antiCheat: {
    tabSwitchDetection: false,
    maxTabSwitches: 3,
    fullscreenRequired: false,
    disableRightClick: false,
    disableCopyPaste: false,
    autoSubmitOnViolation: false,
  },
  assignmentType: "batch",
  assignedBatches: [],
  assignedCourses: [],
  assignedStudents: [],
  startDate: "",
  endDate: "",
};

const emptyDynamicRule = () => ({
  _id: Date.now().toString() + Math.random(),
  category: "",
  subcategory: "",
  difficulty: "",
  tags: [],
  count: 1,
});

const CreateAssessmentPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Reference data
  const [categories, setCategories] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);

  // Question selector state
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [qSearch, setQSearch] = useState("");
  const [qCategory, setQCategory] = useState("");
  const [qDifficulty, setQDifficulty] = useState("");
  const [qLoading, setQLoading] = useState(false);

  // Load reference data
  useEffect(() => {
    Promise.all([
      apiClient.get("/quiz-categories?flat=true").catch(() => ({ data: { categories: [] } })),
      apiClient.get("/batches/list?pageSize=500").catch(() => ({ data: { batches: [] } })),
      apiClient.get("/courses?pageSize=500").catch(() => ({ data: { courses: [] } })),
    ]).then(([catRes, batchRes, courseRes]) => {
      setCategories(catRes.data.categories || []);
      setBatches(batchRes.data.batches || []);
      setCourses(courseRes.data.courses || []);
    });
  }, []);

  // Load assessment for edit
  useEffect(() => {
    if (!isEdit) return;
    setLoadingData(true);
    apiClient.get(`/assessments/${id}`)
      .then(({ data }) => {
        const a = data.assessment;
        setForm({
          title: a.title || "",
          description: a.description || "",
          instructions: a.instructions || "",
          selectionMode: a.selectionMode || "manual",
          questions: (a.questions || []).map(q => ({
            question: q.question?._id || q.question,
            questionText: q.question?.questionText || "",
            questionType: q.question?.questionType || "",
            difficulty: q.question?.difficulty || "",
            marks: q.question?.marks || 1,
            order: q.order || 0,
            overrideMarks: q.overrideMarks,
          })),
          dynamicConfig: a.dynamicConfig || emptyForm.dynamicConfig,
          isSectioned: a.isSectioned || false,
          sections: a.sections || [],
          mode: a.mode || "practice",
          timeLimit: a.timeLimit || 0,
          maxAttempts: a.maxAttempts || 0,
          passingPercentage: a.passingPercentage || 0,
          negativeMarking: a.negativeMarking || false,
          shuffleQuestions: a.shuffleQuestions || false,
          shuffleOptions: a.shuffleOptions || false,
          showResultInstantly: a.showResultInstantly !== false,
          showExplanation: a.showExplanation !== false,
          showCorrectAnswers: a.showCorrectAnswers || false,
          allowReview: a.allowReview !== false,
          antiCheat: { ...emptyForm.antiCheat, ...(a.antiCheat || {}) },
          assignmentType: a.assignmentType || "batch",
          assignedBatches: (a.assignedBatches || []).map(b => b._id || b),
          assignedCourses: (a.assignedCourses || []).map(c => c._id || c),
          assignedStudents: (a.assignedStudents || []).map(s => s._id || s),
          startDate: a.startDate ? a.startDate.slice(0, 16) : "",
          endDate: a.endDate ? a.endDate.slice(0, 16) : "",
        });
      })
      .catch(() => setToast({ show: true, message: "Failed to load assessment", type: "error" }))
      .finally(() => setLoadingData(false));
  }, [id, isEdit]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ─── Question Search (for manual mode) ────────────────────────────
  const searchQuestions = useCallback(async () => {
    setQLoading(true);
    try {
      const params = { limit: 100 };
      if (qSearch) params.search = qSearch;
      if (qCategory) params.category = qCategory;
      if (qDifficulty) params.difficulty = qDifficulty;
      const { data } = await apiClient.get("/assessments/questions-for-selection", { params });
      setAvailableQuestions(data.questions || []);
    } catch { /* silent */ }
    setQLoading(false);
  }, [qSearch, qCategory, qDifficulty]);

  useEffect(() => {
    if (step === 1 && form.selectionMode === "manual") {
      searchQuestions();
    }
  }, [step, form.selectionMode, searchQuestions]);

  const isQuestionSelected = (qId) => form.questions.some(q => (q.question?._id || q.question) === qId);

  const toggleQuestion = (q) => {
    const qId = q._id;
    if (isQuestionSelected(qId)) {
      setForm(prev => ({
        ...prev,
        questions: prev.questions.filter(x => (x.question?._id || x.question) !== qId),
      }));
    } else {
      setForm(prev => ({
        ...prev,
        questions: [
          ...prev.questions,
          {
            question: qId,
            questionText: q.questionText,
            questionType: q.questionType,
            difficulty: q.difficulty,
            marks: q.marks || 1,
            order: prev.questions.length,
            overrideMarks: null,
          },
        ],
      }));
    }
  };

  const removeSelectedQuestion = (qId) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.filter(x => (x.question?._id || x.question) !== qId),
    }));
  };

  // ─── Dynamic Rules ────────────────────────────────────────────────
  const addDynamicRule = () => {
    setForm(prev => ({
      ...prev,
      dynamicConfig: {
        ...prev.dynamicConfig,
        rules: [...prev.dynamicConfig.rules, emptyDynamicRule()],
      },
    }));
  };

  const updateDynamicRule = (index, field, value) => {
    setForm(prev => {
      const rules = [...prev.dynamicConfig.rules];
      rules[index] = { ...rules[index], [field]: value };
      const total = rules.reduce((s, r) => s + (Number.parseInt(r.count, 10) || 0), 0);
      return {
        ...prev,
        dynamicConfig: { ...prev.dynamicConfig, rules, totalQuestions: total },
      };
    });
  };

  const removeDynamicRule = (index) => {
    setForm(prev => {
      const rules = prev.dynamicConfig.rules.filter((_, i) => i !== index);
      const total = rules.reduce((s, r) => s + (Number.parseInt(r.count, 10) || 0), 0);
      return {
        ...prev,
        dynamicConfig: { ...prev.dynamicConfig, rules, totalQuestions: total },
      };
    });
  };

  // ─── Anti-cheat ───────────────────────────────────────────────────
  const updateAntiCheat = (field, value) => {
    setForm(prev => ({
      ...prev,
      antiCheat: { ...prev.antiCheat, [field]: value },
    }));
  };

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSave = async (publish = false) => {
    if (!form.title.trim()) {
      setToast({ show: true, message: "Title is required", type: "error" });
      setStep(0);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        questions: form.questions.map((q, i) => ({
          question: q.question?._id || q.question,
          order: i,
          overrideMarks: q.overrideMarks,
        })),
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };

      let assessmentId;
      if (isEdit) {
        await apiClient.put(`/assessments/${id}`, payload);
        assessmentId = id;
        setToast({ show: true, message: "Assessment updated", type: "success" });
      } else {
        const { data } = await apiClient.post("/assessments", payload);
        assessmentId = data.assessment._id;
        setToast({ show: true, message: "Assessment created", type: "success" });
      }

      if (publish && assessmentId) {
        await apiClient.put(`/assessments/${assessmentId}/publish`);
        setToast({ show: true, message: "Assessment published!", type: "success" });
      }

      setTimeout(() => navigate("/assessments"), 500);
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.message || "Failed to save",
        type: "error",
      });
    }
    setSaving(false);
  };

  // ─── Step navigation ──────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 0) return !!form.title.trim();
    if (step === 1) {
      if (form.selectionMode === "manual") return form.questions.length > 0;
      return form.dynamicConfig.rules.length > 0;
    }
    return true;
  };

  const topCategories = categories.filter(c => !c.parentCategory);

  if (loadingData) {
    return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  }

  // ─── Step renderers ───────────────────────────────────────────────
  const renderStep0_Info = () => (
    <div className="step-content">
      <h5 className="mb-3">Basic Information</h5>
      <Row className="g-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Title *</Form.Label>
            <Form.Control
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="e.g., Weekly Math Quiz, GRE Practice Test"
              required
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Brief description of this assessment"
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group>
            <Form.Label>Instructions <span className="text-muted small">(shown before exam starts)</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.instructions}
              onChange={(e) => updateField("instructions", e.target.value)}
              placeholder="Instructions for students before they begin..."
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Mode *</Form.Label>
            <Form.Select value={form.mode} onChange={(e) => updateField("mode", e.target.value)}>
              <option value="practice">Practice (instant feedback)</option>
              <option value="exam">Exam (results after submission)</option>
              <option value="sectioned_exam">Sectioned Exam (multi-section)</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Question Selection</Form.Label>
            <Form.Select value={form.selectionMode} onChange={(e) => updateField("selectionMode", e.target.value)}>
              <option value="manual">Manual — Pick specific questions</option>
              <option value="dynamic">Dynamic — Auto-generate from rules</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderStep1_Questions = () => (
    <div className="step-content">
      <h5 className="mb-3">
        {form.selectionMode === "manual" ? "Select Questions" : "Dynamic Rules"}
      </h5>

      {form.selectionMode === "manual" ? (
        <>
          {/* Question Picker */}
          <div className="question-selector">
            <div className="question-selector__toolbar">
              <Form.Control
                size="sm"
                placeholder="Search questions..."
                value={qSearch}
                onChange={(e) => setQSearch(e.target.value)}
                className="flex-grow-1"
              />
              <Form.Select size="sm" value={qCategory} onChange={(e) => setQCategory(e.target.value)} className="w-auto">
                <option value="">All Categories</option>
                {topCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </Form.Select>
              <Form.Select size="sm" value={qDifficulty} onChange={(e) => setQDifficulty(e.target.value)} className="w-auto">
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </Form.Select>
              <Button size="sm" variant="outline-primary" onClick={searchQuestions} disabled={qLoading}>
                <Search size={14} />
              </Button>
            </div>
            <div className="question-selector__list">
              {qLoading ? (
                <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
              ) : availableQuestions.length === 0 ? (
                <div className="text-center text-muted py-3">No questions found</div>
              ) : (
                availableQuestions.map(q => (
                  <div
                    key={q._id}
                    className={`question-selector__item ${isQuestionSelected(q._id) ? "question-selector__item--selected" : ""}`}
                    onClick={() => toggleQuestion(q)}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={isQuestionSelected(q._id)}
                      onChange={() => toggleQuestion(q)}
                      className="me-0"
                    />
                    <span className="q-text">{q.questionText}</span>
                    <span className="q-meta">
                      <span className={`q-badge q-badge--${q.difficulty}`}>{q.difficulty}</span>
                      <span className="q-badge q-badge--type">{QUESTION_TYPE_LABELS[q.questionType] || q.questionType}</span>
                      <span className="q-badge q-badge--type">{q.marks}m</span>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Selected Questions */}
          {form.questions.length > 0 && (
            <div className="selected-questions">
              <div className="selected-questions__header">
                <span>{form.questions.length} questions selected</span>
                <span className="text-muted small">
                  Total marks: {form.questions.reduce((s, q) => s + (q.overrideMarks ?? q.marks ?? 1), 0)}
                </span>
              </div>
              <div className="selected-questions__list">
                {form.questions.map((q, idx) => (
                  <div key={q.question?._id || q.question} className="selected-questions__item">
                    <span className="order-num">{idx + 1}</span>
                    <span className="q-text">{q.questionText || "Question"}</span>
                    <span className={`q-badge q-badge--${q.difficulty}`}>{q.difficulty}</span>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      onClick={() => removeSelectedQuestion(q.question?._id || q.question)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Dynamic Rules */}
          <div className="dynamic-rules">
            {form.dynamicConfig.rules.map((rule, idx) => (
              <div key={rule._id || idx} className="dynamic-rules__rule">
                <Form.Select
                  size="sm"
                  value={rule.category || ""}
                  onChange={(e) => updateDynamicRule(idx, "category", e.target.value)}
                >
                  <option value="">Any Category</option>
                  {topCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={rule.difficulty || ""}
                  onChange={(e) => updateDynamicRule(idx, "difficulty", e.target.value)}
                >
                  <option value="">Any Difficulty</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </Form.Select>
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  value={rule.count}
                  onChange={(e) => updateDynamicRule(idx, "count", Number.parseInt(e.target.value, 10) || 1)}
                  placeholder="Count"
                />
                <span className="text-muted small text-nowrap">questions</span>
                <button type="button" className="btn btn-link p-0 text-danger" onClick={() => removeDynamicRule(idx)}>
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
          <Button variant="link" size="sm" className="mt-2 p-0" onClick={addDynamicRule}>
            <Plus size={14} className="me-1" /> Add Rule
          </Button>
          {form.dynamicConfig.totalQuestions > 0 && (
            <div className="mt-3 text-muted small">
              Total: <strong>{form.dynamicConfig.totalQuestions}</strong> questions will be generated per attempt
            </div>
          )}
          <Row className="g-3 mt-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Avoid questions seen in last N days</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  min={0}
                  value={form.dynamicConfig.avoidRecentDays}
                  onChange={(e) =>
                    setForm(prev => ({
                      ...prev,
                      dynamicConfig: {
                        ...prev.dynamicConfig,
                        avoidRecentDays: Number.parseInt(e.target.value, 10) || 0,
                      },
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  const renderStep2_Settings = () => (
    <div className="step-content">
      <h5 className="mb-3">Quiz Settings</h5>
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Time Limit (minutes, 0 = no limit)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={form.timeLimit}
              onChange={(e) => updateField("timeLimit", Number.parseInt(e.target.value, 10) || 0)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Max Attempts (0 = unlimited)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={form.maxAttempts}
              onChange={(e) => updateField("maxAttempts", Number.parseInt(e.target.value, 10) || 0)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Passing % (0 = no pass/fail)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={100}
              value={form.passingPercentage}
              onChange={(e) => updateField("passingPercentage", Number.parseInt(e.target.value, 10) || 0)}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="settings-grid">
        {[
          { key: "negativeMarking", label: "Negative Marking", hint: "Deduct marks for wrong answers" },
          { key: "shuffleQuestions", label: "Shuffle Questions", hint: "Randomize question order per student" },
          { key: "shuffleOptions", label: "Shuffle Options", hint: "Randomize option order" },
          { key: "showResultInstantly", label: "Show Result Instantly", hint: "Show score immediately after submission" },
          { key: "showExplanation", label: "Show Explanations", hint: "Show answer explanations" },
          { key: "showCorrectAnswers", label: "Show Correct Answers", hint: "Reveal correct answers after submission" },
          { key: "allowReview", label: "Allow Review", hint: "Let students review their answers after submission" },
        ].map((s) => (
          <div className="setting-item" key={s.key}>
            <div>
              <div className="setting-item__label">{s.label}</div>
              <div className="setting-item__hint">{s.hint}</div>
            </div>
            <Form.Check
              type="switch"
              checked={form[s.key]}
              onChange={(e) => updateField(s.key, e.target.checked)}
            />
          </div>
        ))}
      </div>

      {/* Anti-Cheat (only for exam mode) */}
      {(form.mode === "exam" || form.mode === "sectioned_exam") && (
        <div className="anti-cheat-section mt-4">
          <div className="anti-cheat-section__title">
            <Shield size={16} /> Anti-Cheat Settings
          </div>
          <div className="settings-grid">
            {[
              { key: "tabSwitchDetection", label: "Tab Switch Detection" },
              { key: "fullscreenRequired", label: "Fullscreen Required" },
              { key: "disableRightClick", label: "Disable Right Click" },
              { key: "disableCopyPaste", label: "Disable Copy/Paste" },
              { key: "autoSubmitOnViolation", label: "Auto-Submit on Violation" },
            ].map((s) => (
              <div className="setting-item" key={s.key}>
                <div className="setting-item__label">{s.label}</div>
                <Form.Check
                  type="switch"
                  checked={form.antiCheat[s.key]}
                  onChange={(e) => updateAntiCheat(s.key, e.target.checked)}
                />
              </div>
            ))}
          </div>
          {form.antiCheat.tabSwitchDetection && (
            <Row className="g-3 mt-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small">Max Tab Switches</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    value={form.antiCheat.maxTabSwitches}
                    onChange={(e) => updateAntiCheat("maxTabSwitches", Number.parseInt(e.target.value, 10) || 3)}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );

  const renderStep3_Assignment = () => (
    <div className="step-content">
      <h5 className="mb-3">Assignment & Scheduling</h5>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Assign To</Form.Label>
            <Form.Select
              value={form.assignmentType}
              onChange={(e) => {
                updateField("assignmentType", e.target.value);
                updateField("assignedBatches", []);
                updateField("assignedCourses", []);
              }}
            >
              <option value="batch">Batches</option>
              <option value="course">Courses</option>
              <option value="open">Open (all students)</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {form.assignmentType === "batch" && (
        <div className="mt-3">
          <Form.Label>Select Batches</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {batches.map((b) => (
              <Form.Check
                key={b._id}
                type="checkbox"
                label={b.name}
                checked={form.assignedBatches.includes(b._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateField("assignedBatches", [...form.assignedBatches, b._id]);
                  } else {
                    updateField("assignedBatches", form.assignedBatches.filter(x => x !== b._id));
                  }
                }}
              />
            ))}
            {batches.length === 0 && <span className="text-muted small">No batches found</span>}
          </div>
        </div>
      )}

      {form.assignmentType === "course" && (
        <div className="mt-3">
          <Form.Label>Select Courses</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {courses.map((c) => (
              <Form.Check
                key={c._id}
                type="checkbox"
                label={c.title || c.name}
                checked={form.assignedCourses.includes(c._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateField("assignedCourses", [...form.assignedCourses, c._id]);
                  } else {
                    updateField("assignedCourses", form.assignedCourses.filter(x => x !== c._id));
                  }
                }}
              />
            ))}
            {courses.length === 0 && <span className="text-muted small">No courses found</span>}
          </div>
        </div>
      )}

      <Row className="g-3 mt-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Start Date <span className="text-muted small">(optional)</span></Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.startDate}
              onChange={(e) => updateField("startDate", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>End Date <span className="text-muted small">(optional)</span></Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.endDate}
              onChange={(e) => updateField("endDate", e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderStep4_Review = () => {
    const totalMarks = form.selectionMode === "manual"
      ? form.questions.reduce((s, q) => s + (q.overrideMarks ?? q.marks ?? 1), 0)
      : form.dynamicConfig.totalQuestions;

    return (
      <div className="step-content">
        <h5 className="mb-3">Review & Submit</h5>
        <div className="review-summary">
          <div className="review-summary__card">
            <div className="review-summary__card-title">Basic Info</div>
            <div className="review-summary__card-value">{form.title}</div>
            <ul className="review-summary__card-list mt-2">
              <li><span>Mode</span> <strong>{MODE_LABELS[form.mode]}</strong></li>
              <li><span>Selection</span> <strong>{form.selectionMode === "manual" ? "Manual" : "Dynamic"}</strong></li>
            </ul>
          </div>

          <div className="review-summary__card">
            <div className="review-summary__card-title">Questions</div>
            <ul className="review-summary__card-list">
              {form.selectionMode === "manual" ? (
                <li><span>Selected</span> <strong>{form.questions.length}</strong></li>
              ) : (
                <>
                  <li><span>Rules</span> <strong>{form.dynamicConfig.rules.length}</strong></li>
                  <li><span>Per attempt</span> <strong>{form.dynamicConfig.totalQuestions}</strong></li>
                </>
              )}
              <li><span>Total Marks</span> <strong>{totalMarks}</strong></li>
            </ul>
          </div>

          <div className="review-summary__card">
            <div className="review-summary__card-title">Settings</div>
            <ul className="review-summary__card-list">
              <li><span>Time Limit</span> <strong>{form.timeLimit > 0 ? `${form.timeLimit} min` : "None"}</strong></li>
              <li><span>Max Attempts</span> <strong>{form.maxAttempts > 0 ? form.maxAttempts : "Unlimited"}</strong></li>
              <li><span>Passing %</span> <strong>{form.passingPercentage > 0 ? `${form.passingPercentage}%` : "None"}</strong></li>
              <li><span>Negative Marking</span> <strong>{form.negativeMarking ? "Yes" : "No"}</strong></li>
              <li><span>Shuffle</span> <strong>{form.shuffleQuestions ? "Yes" : "No"}</strong></li>
            </ul>
          </div>

          <div className="review-summary__card">
            <div className="review-summary__card-title">Assignment</div>
            <ul className="review-summary__card-list">
              <li><span>Assign to</span> <strong>{form.assignmentType}</strong></li>
              {form.assignmentType === "batch" && (
                <li><span>Batches</span> <strong>{form.assignedBatches.length} selected</strong></li>
              )}
              {form.assignmentType === "course" && (
                <li><span>Courses</span> <strong>{form.assignedCourses.length} selected</strong></li>
              )}
              {form.startDate && <li><span>Start</span> <strong>{new Date(form.startDate).toLocaleString()}</strong></li>}
              {form.endDate && <li><span>End</span> <strong>{new Date(form.endDate).toLocaleString()}</strong></li>}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const stepRenderers = [renderStep0_Info, renderStep1_Questions, renderStep2_Settings, renderStep3_Assignment, renderStep4_Review];

  return (
    <div className="create-assessment-page">
      <PageHeader
        backButton
        icon={ClipboardCheck}
        title={isEdit ? "Edit Assessment" : "Create Assessment"}
        description="Multi-step assessment builder"
      />

      {/* Step Indicator */}
      <div className="step-indicator">
        {STEPS.map((s, idx) => (
          <button
            type="button"
            key={s.key}
            className={`step-indicator__step ${idx === step ? "step-indicator__step--active" : ""} ${idx < step ? "step-indicator__step--completed" : ""}`}
            onClick={() => setStep(idx)}
          >
            <span className="step-number">
              {idx < step ? <Check size={14} /> : idx + 1}
            </span>
            <span className="step-label">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Step Content */}
      <Form onSubmit={(e) => e.preventDefault()}>
        {stepRenderers[step]()}

        {/* Navigation */}
        <div className="step-actions">
          <Button
            variant="light"
            onClick={() => (step === 0 ? navigate("/assessments") : setStep(step - 1))}
          >
            {step === 0 ? "Cancel" : <><ChevronLeft size={16} /> Back</>}
          </Button>
          <div className="d-flex gap-2">
            {step === STEPS.length - 1 ? (
              <>
                <Button variant="outline-primary" onClick={() => handleSave(false)} disabled={saving}>
                  {saving ? <Spinner size="sm" animation="border" /> : "Save as Draft"}
                </Button>
                <Button variant="primary" onClick={() => handleSave(true)} disabled={saving}>
                  {saving ? <Spinner size="sm" animation="border" /> : "Save & Publish"}
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => setStep(step + 1)}
                disabled={!canGoNext()}
              >
                Next <ChevronRight size={16} />
              </Button>
            )}
          </div>
        </div>
      </Form>

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default CreateAssessmentPage;
